import { useEffect, useState, useRef } from 'react'
import { motion } from 'framer-motion'
import { User, Briefcase, Phone, Camera, X } from 'lucide-react'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useAuthStore, Input, Select, cn } from '@personic/shared'

// Common timezones
const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'Europe/London', label: 'Greenwich Mean Time (GMT)' },
  { value: 'Europe/Paris', label: 'Central European Time (CET)' },
  { value: 'Europe/Berlin', label: 'Central European Time (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan Standard Time (JST)' },
  { value: 'Asia/Shanghai', label: 'China Standard Time (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore Time (SGT)' },
  { value: 'Asia/Dubai', label: 'Gulf Standard Time (GST)' },
  { value: 'Australia/Sydney', label: 'Australian Eastern Time (AET)' },
  { value: 'Pacific/Auckland', label: 'New Zealand Time (NZT)' },
]

export default function ProfileSetup() {
  const { user } = useAuthStore()
  const { profileData, setProfileData, setStep } = useOnboardingStore()
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Ensure we're on step 2 and pre-fill name from user
  useEffect(() => {
    setStep(2)
    if (user?.name && !profileData.fullName) {
      setProfileData({ fullName: user.name })
    }
  }, [setStep, user?.name, profileData.fullName, setProfileData])

  const handleChange = (field: string) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setProfileData({ [field]: e.target.value })
  }

  const handleAvatarClick = () => {
    fileInputRef.current?.click()
  }

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string)
        setProfileData({ avatarUrl: reader.result as string })
      }
      reader.readAsDataURL(file)
    }
  }

  const handleRemoveAvatar = () => {
    setAvatarPreview(null)
    setProfileData({ avatarUrl: undefined })
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  // Get initials for avatar placeholder
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-midnight-900 mb-2">
          Set up your profile
        </h1>
        <p className="text-warm-600">
          Help us personalize your experience with some basic information.
        </p>
      </motion.div>

      {/* Avatar Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-col items-center mb-8"
      >
        <div className="relative">
          <button
            type="button"
            onClick={handleAvatarClick}
            className={cn(
              'w-24 h-24 rounded-full flex items-center justify-center',
              'border-2 border-dashed border-warm-300 hover:border-copper-400',
              'transition-all duration-200 overflow-hidden',
              'focus:outline-none focus:ring-2 focus:ring-copper-400 focus:ring-offset-2'
            )}
          >
            {avatarPreview || profileData.avatarUrl ? (
              <img
                src={avatarPreview || profileData.avatarUrl}
                alt="Avatar preview"
                className="w-full h-full object-cover"
              />
            ) : profileData.fullName ? (
              <span className="text-2xl font-heading font-bold text-midnight-600 bg-midnight-100 w-full h-full flex items-center justify-center">
                {getInitials(profileData.fullName)}
              </span>
            ) : (
              <div className="flex flex-col items-center text-warm-400">
                <Camera className="w-8 h-8 mb-1" />
              </div>
            )}
          </button>

          {/* Remove Avatar Button */}
          {(avatarPreview || profileData.avatarUrl) && (
            <button
              type="button"
              onClick={handleRemoveAvatar}
              className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white hover:bg-red-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            className="hidden"
          />
        </div>
        <p className="mt-2 text-sm text-warm-500">
          Click to upload a photo (optional)
        </p>
      </motion.div>

      {/* Form Fields */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-5"
      >
        <Input
          label="Full Name"
          value={profileData.fullName}
          onChange={handleChange('fullName')}
          placeholder="John Doe"
          required
          leftIcon={<User size={18} />}
        />

        <Input
          label="Job Title"
          value={profileData.jobTitle}
          onChange={handleChange('jobTitle')}
          placeholder="Product Manager"
          leftIcon={<Briefcase size={18} />}
          hint="Your role within the organization"
        />

        <Input
          label="Phone Number"
          type="tel"
          value={profileData.phone}
          onChange={handleChange('phone')}
          placeholder="+1 (555) 123-4567"
          leftIcon={<Phone size={18} />}
          hint="Optional - for account recovery"
        />

        <Select
          label="Timezone"
          value={profileData.timezone}
          onChange={handleChange('timezone')}
          options={TIMEZONES}
        />
      </motion.div>

      {/* Validation Hint */}
      {!profileData.fullName && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mt-6 text-sm text-warm-500 text-center"
        >
          Please enter your name to continue
        </motion.p>
      )}
    </div>
  )
}
